/**
 * VRG-740 — GTM → Articles → Ideas: the CONTENT IDEAS QUEUE, and the VERAfied guest list.
 *
 * An idea is a piece Vera might write (an /learn explainer, an /insights perspective, or a VERAfied
 * interview), with the search terms it targets, why it is worth writing, the research behind it, and
 * where it stands. Ideas arrive by hand, from the one-off import of the old article pipeline
 * (vera-inc WIP commit 604d920, `articles/data/pipeline.csv`), and automatically from the SEO
 * Recommendations recompute (competitor gap terms, rising terms with no page, AI answers that cite a
 * competitor and not Vera).
 *
 * Text + CHECK constraints in migration 0168 (not Postgres enums), the GTM convention (0133 / 0137 /
 * 0147). JSON for `/internal/gtm/content-ideas/*` is snake_case,
 * field names EXACTLY the column names, like GTM → Social.
 *
 * Solutions are `MetricSolution` values (the website's six solutions, snake_case). Keyword taxonomy in
 * `seo_keywords.solutions` uses the kebab spelling of the SAME six; `solutionFromSeoSlug` converts.
 */
import {MetricSolution} from '../analytics/metric-taxonomy.js';

/**
 * What the idea BECOMES — a genre, not a destination. `interview` was `verafied` until 2026-10-29
 * (Spencer: "I think type is interview?"); migration 0173 renamed the value and the CHECK.
 *
 * Two reasons it is not the series name. The axis is editorial: we do not call a perspective
 * "Insights" after the section it lands in, so an interview is not "VERAfied" after the series it
 * lands in — and the tab it feeds has read "Interviews" since VRG-751, so a value and the surface
 * now agree. Second, `social-lint.ts` BLOCKS /verafied/i in public copy ("held for counsel,
 * trademark check"); a type travels into exports and error strings, and a blocked word is the
 * wrong thing to put on that road.
 *
 * The ORIGIN `verafied_followup` deliberately keeps the series name: origin says where an idea CAME
 * FROM (an episode we recorded), type says what it will be. Different questions, different words.
 */
export const CONTENT_IDEA_TYPES = ['explainer', 'perspective', 'interview'] as const;
export type ContentIdeaType = (typeof CONTENT_IDEA_TYPES)[number];
export const CONTENT_IDEA_TYPE_LABELS: Record<ContentIdeaType, string> = {
  explainer: 'Explainer',
  perspective: 'Perspective',
  interview: 'Interview',
};

export const CONTENT_IDEA_STATUSES = [
  'idea',
  'brief',
  'draft',
  'approved',
  'published',
  'rejected',
] as const;
export type ContentIdeaStatus = (typeof CONTENT_IDEA_STATUSES)[number];

/**
 * Where an idea came from. `seo_gap`, `rising_term` and `aeo_uncited` are fed automatically after
 * every SEO Recommendations recompute; the other origins have hooks and no data source yet (task file).
 */
export const CONTENT_IDEA_ORIGINS = [
  'seo_gap',
  'rising_term',
  'aeo_uncited',
  'competitor_post',
  'regulation',
  'deal_question',
  'verafied_followup',
  'manual',
  // VRG-741 — a new corpus item (market_items) from a source that is neither a competitor's feed nor
  // regulatory, mapped to tracked SEO terms.
  'market_item',
] as const;
export type ContentIdeaOrigin = (typeof CONTENT_IDEA_ORIGINS)[number];
export const CONTENT_IDEA_ORIGIN_LABELS: Record<ContentIdeaOrigin, string> = {
  seo_gap: 'Competitor gap',
  rising_term: 'Rising term',
  aeo_uncited: 'AI answer, Vera not cited',
  competitor_post: 'Competitor post',
  regulation: 'Regulation',
  deal_question: 'Deal question',
  verafied_followup: 'VERAfied follow-up',
  manual: 'Manual',
  market_item: 'Corpus item',
};
/** The origins the API writes on its own. A person may not create an idea claiming one of them. */
export const CONTENT_IDEA_AUTO_ORIGINS: readonly ContentIdeaOrigin[] = [
  'seo_gap',
  'rising_term',
  'aeo_uncited',
  'market_item',
];
/**
 * VRG-741 — the origins the CORPUS feed writes: a new `market_items` row becomes `competitor_post`
 * when its source names a competitor, `regulation` when its source is regulatory, else
 * `market_item`. A person may still log a competitor post or a regulation by hand.
 */
export const CONTENT_IDEA_CORPUS_ORIGINS: readonly ContentIdeaOrigin[] = [
  'competitor_post',
  'regulation',
  'market_item',
];

/** The keyword master's priority scale (vera-inc gtm/channels/seo/keywords-master.csv). */
export const CONTENT_IDEA_PRIORITIES = ['P1', 'P2', 'P3'] as const;
export type ContentIdeaPriority = (typeof CONTENT_IDEA_PRIORITIES)[number];

/**
 * The status machine. Forward one step, back one step, reject from any open state, reopen a
 * rejected idea. ONE approval, and it is on the DRAFT (Spencer, 2026-10-23): moving `idea → brief`
 * ("Start brief") is the selection, and there is no "approve idea" step; `approved` is reachable
 * only from `draft`. `published` needs the article (section + slug) it became; leaving `published`
 * keeps the slug so the link survives an unpublish.
 */
export const CONTENT_IDEA_TRANSITIONS: Record<ContentIdeaStatus, readonly ContentIdeaStatus[]> = {
  idea: ['brief', 'rejected'],
  brief: ['draft', 'idea', 'rejected'],
  draft: ['approved', 'brief', 'rejected'],
  approved: ['published', 'draft', 'rejected'],
  published: ['approved'],
  rejected: ['idea'],
};
export const contentIdeaTransitionAllowed = (
  from: ContentIdeaStatus,
  to: ContentIdeaStatus,
): boolean => CONTENT_IDEA_TRANSITIONS[from]?.includes(to) ?? false;

export const isContentIdeaType = (v: unknown): v is ContentIdeaType =>
  typeof v === 'string' && (CONTENT_IDEA_TYPES as readonly string[]).includes(v);
export const isContentIdeaStatus = (v: unknown): v is ContentIdeaStatus =>
  typeof v === 'string' && (CONTENT_IDEA_STATUSES as readonly string[]).includes(v);
export const isContentIdeaOrigin = (v: unknown): v is ContentIdeaOrigin =>
  typeof v === 'string' && (CONTENT_IDEA_ORIGINS as readonly string[]).includes(v);
export const isContentIdeaPriority = (v: unknown): v is ContentIdeaPriority =>
  typeof v === 'string' && (CONTENT_IDEA_PRIORITIES as readonly string[]).includes(v);

export const CONTENT_SOLUTIONS: readonly MetricSolution[] = Object.values(MetricSolution);
export const isContentSolution = (v: unknown): v is MetricSolution =>
  typeof v === 'string' && (CONTENT_SOLUTIONS as readonly string[]).includes(v);
/** `brand-protection` (seo_keywords.solutions) → `brand_protection` (MetricSolution). */
export const solutionFromSeoSlug = (slug: string): MetricSolution | null => {
  const v = slug.trim().toLowerCase().replace(/-/g, '_');
  return isContentSolution(v) ? v : null;
};

/**
 * The website's 16 published industries plus `cross-industry`, as the SEO keyword taxonomy spells
 * them (apps/api/src/features/seo/seo-taxonomy.ts INDUSTRY_SLUGS + CROSS_INDUSTRY). A guard pins the
 * two lists equal, so an industry added on one side fails until it is added on the other.
 */
export const CONTENT_INDUSTRIES = [
  'apparel',
  'art',
  'auction',
  'eyewear',
  'footwear',
  'handbags',
  'jewelry',
  'legal-documents',
  'memorabilia',
  'music',
  'perfume',
  'supplements',
  'ticketing',
  'trading-cards',
  'watches',
  'wines-and-spirits',
  'cross-industry',
] as const;
export type ContentIndustry = (typeof CONTENT_INDUSTRIES)[number];
export const CONTENT_INDUSTRY_LABELS: Record<ContentIndustry, string> = {
  apparel: 'Apparel',
  art: 'Art',
  auction: 'Auction',
  eyewear: 'Eyewear',
  footwear: 'Footwear',
  handbags: 'Handbags',
  jewelry: 'Jewelry',
  'legal-documents': 'Legal documents',
  memorabilia: 'Memorabilia',
  music: 'Music',
  perfume: 'Perfume',
  supplements: 'Supplements',
  ticketing: 'Ticketing',
  'trading-cards': 'Trading cards',
  watches: 'Watches',
  'wines-and-spirits': 'Wines & spirits',
  'cross-industry': 'Cross-industry',
};
export const isContentIndustry = (v: unknown): v is ContentIndustry =>
  typeof v === 'string' && (CONTENT_INDUSTRIES as readonly string[]).includes(v);

/**
 * The dedupe key for a search term: lowercase, punctuation to spaces, whitespace collapsed.
 * "Digital Product Passport  Requirements?" and "digital product passport requirements" are one
 * term. Variants ("…requirement" vs "…requirements") are NOT folded; the keyword master already
 * folds them into one row's `variants`, and the feed checks those too.
 */
export const contentTermKey = (term: string): string =>
  term
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** The article sections the website's manifest emits (same as GTM → Articles). */
export const CONTENT_ARTICLE_SECTIONS = ['insights', 'learn'] as const;
export type ContentArticleSection = (typeof CONTENT_ARTICLE_SECTIONS)[number];

export interface ContentIdeaTerm {
  id: string;
  term: string;
  /** The `seo_keywords` row when the term is in the keyword master (or a proposed keyword). */
  keyword_id: string | null;
  cluster: string | null;
  intent: string | null;
  market: string | null;
  is_primary: boolean;
  /** Read from the keyword master at request time (null when the term is not in it). */
  keyword_priority: string | null;
  keyword_buyer_fit: number | null;
  keyword_target_url: string | null;
  keyword_status: string | null;
}

/** A research link: a `market_items` row (the research corpus) attached to an idea. */
export interface ContentIdeaSource {
  market_item_id: string;
  url: string;
  title: string | null;
  publication: string | null;
  published_at: string | null;
  solution: string | null;
  featured: ContentSourceFeatured | null;
  note: string | null;
}

export const CONTENT_SOURCE_FEATURED = ['primary', 'pick'] as const;
export type ContentSourceFeatured = (typeof CONTENT_SOURCE_FEATURED)[number];

export interface ContentIdeaEvent {
  id: string;
  from_status: ContentIdeaStatus | null;
  to_status: ContentIdeaStatus;
  actor: string | null;
  note: string | null;
  at: string;
}

export interface ContentIdea {
  id: string;
  title: string;
  type: ContentIdeaType;
  status: ContentIdeaStatus;
  origin: ContentIdeaOrigin;
  /** The originating record, as text (an SEO recommendation's dedupe key, a CSV row, a deal id…). */
  origin_ref: string | null;
  seo_recommendation_id: string | null;
  solutions: MetricSolution[];
  industries: ContentIndustry[];
  rationale: string | null;
  notes: string | null;
  draft_url: string | null;
  priority: ContentIdeaPriority | null;
  /** Ordering only, never a forecast. How it was derived is in `score_basis`. */
  score: number | null;
  score_basis: Record<string, unknown> | null;
  published_section: ContentArticleSection | null;
  published_slug: string | null;
  /** VRG-741 — the recommendation card "Suggest 5 more" assembled, or null. */
  recommendation: ContentRecommendation | null;
  recommended_at: string | null;
  recommendation_batch_id: string | null;
  terms: ContentIdeaTerm[];
  sources: ContentIdeaSource[];
  /** Detail reads only (`GET /content-ideas/:id`); the list returns []. */
  events: ContentIdeaEvent[];
  status_changed_at: string;
  status_changed_by: string | null;
  approved_at: string | null;
  approved_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export interface ContentIdeaTermInput {
  term: string;
  keyword_id?: string | null;
  cluster?: string | null;
  intent?: string | null;
  market?: string | null;
}

export interface CreateContentIdeaRequest {
  title: string;
  type: ContentIdeaType;
  origin?: ContentIdeaOrigin;
  origin_ref?: string | null;
  solutions?: MetricSolution[];
  industries?: ContentIndustry[];
  rationale?: string | null;
  notes?: string | null;
  draft_url?: string | null;
  priority?: ContentIdeaPriority | null;
  /** The first is the primary term. Replaces the whole set on update. */
  terms?: ContentIdeaTermInput[];
}
export type UpdateContentIdeaRequest = Partial<CreateContentIdeaRequest>;

export interface SetContentIdeaStatusRequest {
  to: ContentIdeaStatus;
  note?: string | null;
  /** Required with `to: 'published'` unless the idea already carries them. */
  published_section?: ContentArticleSection;
  published_slug?: string;
}

export interface AddContentIdeaSourceRequest {
  /** An existing research corpus row… */
  market_item_id?: string;
  /** …or a URL: matched to the corpus by normalised URL, else added to it (no fetch). */
  url?: string;
  title?: string | null;
  note?: string | null;
}

export interface ContentIdeaFeedResult {
  ran_at: string;
  created: number;
  terms_added: number;
  skipped: Record<string, number>;
}

// ── VRG-741: the corpus feed, its run log, and recommendation cards ─────────────────────────────

export const CONTENT_SOURCE_CATEGORIES = ['market', 'regulatory'] as const;
export type ContentSourceCategory = (typeof CONTENT_SOURCE_CATEGORIES)[number];
export const CONTENT_SOURCE_CATEGORY_LABELS: Record<ContentSourceCategory, string> = {
  market: 'Market',
  regulatory: 'Regulatory',
};

export const CONTENT_FEED_TRIGGERS = ['seo_recompute', 'source_poll', 'manual'] as const;
export type ContentFeedTrigger = (typeof CONTENT_FEED_TRIGGERS)[number];
export const CONTENT_FEED_TRIGGER_LABELS: Record<ContentFeedTrigger, string> = {
  seo_recompute: 'SEO recompute',
  source_poll: 'Source poll',
  manual: 'Run by hand',
};

/** What the corpus feed did with one item (`market_items.ideas_feed_outcome`). */
export const CORPUS_FEED_OUTCOMES = ['idea', 'merged', 'unmapped', 'skipped', 'baseline', 'backfill'] as const;
export type CorpusFeedOutcome = (typeof CORPUS_FEED_OUTCOMES)[number];

/** A corpus item that mapped to no tracked SEO term: a candidate NEW keyword, for review only. */
export interface UnmappedCorpusItem {
  market_item_id: string;
  url: string;
  title: string | null;
  publication: string | null;
  source_name: string | null;
  /** Phrases from the title that are not in the keyword master (candidate keywords, never auto-added). */
  phrases: string[];
  fed_at: string | null;
}

export interface CorpusFeedResult {
  items_seen: number;
  ideas_created: number;
  /** Items whose best term was already on an open idea: attached to it as research. */
  ideas_merged: number;
  guests_suggested: number;
  unmapped_items: number;
  /** VRG-741 flood guard: backlog items (published 90+ days before ingest, or undated on a source's first poll) held as reference only. */
  backfill_items: number;
  skipped: Record<string, number>;
}

/** One feed run (`gtm_content_feed_runs`): the SEO half, the corpus half, or both. */
export interface ContentFeedRun {
  id: string;
  trigger: ContentFeedTrigger;
  actor: string | null;
  items_seen: number;
  ideas_created: number;
  ideas_merged: number;
  guests_suggested: number;
  unmapped_items: number;
  /** VRG-741 flood guard: items held back as reference only. */
  backfill_items: number;
  seo: ContentIdeaFeedResult | null;
  skipped: Record<string, number>;
  unmapped: UnmappedCorpusItem[];
  error: string | null;
  ran_at: string;
}

/** `POST /content-ideas/feed`: both feeds, and the run row they were logged as. */
export interface ContentFeedRunResult {
  run: ContentFeedRun;
}

/**
 * Planned graphic kinds. The first four are the website's infographic figures (vera-website
 * `content/article-blocks.ts` FigureBlock, branch agent/article-infographics, 2026-10-23):
 * figureTimeline | figureCompare | figureFlow | figureNumbers. `bar` and `line` are DATA CHARTS the
 * website has not built yet (editorial-style.md §5): they may be planned, flagged "needs chart block".
 */
export const CONTENT_GRAPHIC_KINDS = ['timeline', 'compare', 'flow', 'numbers', 'bar', 'line'] as const;
export type ContentGraphicKind = (typeof CONTENT_GRAPHIC_KINDS)[number];
export const CONTENT_GRAPHIC_CHART_KINDS: readonly ContentGraphicKind[] = ['bar', 'line'];
export const CONTENT_GRAPHIC_KIND_LABELS: Record<ContentGraphicKind, string> = {
  timeline: 'Timeline',
  compare: 'Comparison',
  flow: 'Process',
  numbers: 'In numbers',
  bar: 'Bar chart',
  line: 'Line chart',
};
/** The website block each kind is drawn with (`figureBar` / `figureLine` do not exist yet). */
export const CONTENT_GRAPHIC_BLOCK_TYPES: Record<ContentGraphicKind, string> = {
  timeline: 'figureTimeline',
  compare: 'figureCompare',
  flow: 'figureFlow',
  numbers: 'figureNumbers',
  bar: 'figureBar',
  line: 'figureLine',
};

export interface ContentRecommendationTerm {
  term: string;
  keyword_id: string | null;
  /** Monthly searches in `market`, from the keyword master's latest metrics; null when unmeasured. */
  volume: number | null;
  market: string | null;
  kd: number | null;
}

export interface ContentRecommendationSource {
  market_item_id: string;
  url: string;
  title: string | null;
  publication: string | null;
  published_at: string | null;
  /** Why it is research for this piece: the terms it mentions. */
  matched_terms: string[];
}

export interface ContentPlannedGraphic {
  kind: ContentGraphicKind;
  /** The website block it would be drawn with (figureTimeline, …). */
  block_type: string;
  /** A data chart (bar, line): the website has no chart block yet, so it needs one built first. */
  needs_chart_block: boolean;
  /** What it shows, in one sentence. */
  shows: string;
  /** The corpus item that supplies its data. A graphic with no data source is never proposed. */
  source: {market_item_id: string; title: string | null; url: string};
  /** The evidence the detector found in that source (dates, figures, named rivals, steps). */
  evidence: string[];
  /** The outline section it belongs to. */
  section: string;
}

export interface ContentOutlineSection {
  heading: string;
  /** What the section must do, from the editorial style guide. */
  guidance: string;
  /** Research items that serve this section (corpus ids). */
  research: string[];
}

/**
 * VRG-741 — the RECOMMENDATION card: what "Suggest 5 more" assembles for an idea, from templates and
 * the database alone (no model call). Spencer chooses "Write this" (the idea moves to Brief, the selection) or
 * rejects it with a reason (the idea is rejected; its terms never re-feed).
 */
export interface ContentRecommendation {
  version: 1;
  title: string;
  /** 2–3 sentences: what the piece would argue (perspective) or answer (explainer). */
  summary: string;
  type: Exclude<ContentIdeaType, 'verafied'>;
  /** Why that type, by the site rule (vera-website content/learn.ts). */
  type_reason: string;
  primary_term: ContentRecommendationTerm | null;
  secondary_terms: ContentRecommendationTerm[];
  industries: ContentIndustry[];
  solutions: MetricSolution[];
  graphics: ContentPlannedGraphic[];
  /** Set when fewer graphics than the guide asks for could be sourced (never padded). */
  graphics_note: string | null;
  sources: ContentRecommendationSource[];
  outline: ContentOutlineSection[];
  /** The editorial style guide the outline follows (path + date read). */
  style_guide: string;
  generated_at: string;
}

/** The batch-level variety constraints, in the order they are RELAXED when the pool is too thin. */
export const RECOMMENDATION_RELAXATION_ORDER = [
  'industry_vs_recent',
  'solution_span',
  'type_mix',
  'industry_in_batch',
  'term_vs_recent',
] as const;
export type RecommendationConstraint = (typeof RECOMMENDATION_RELAXATION_ORDER)[number];
export const RECOMMENDATION_CONSTRAINT_LABELS: Record<RecommendationConstraint, string> = {
  industry_vs_recent: 'No primary industry used by a piece approved or published in the last 90 days',
  solution_span: 'The batch spans at least 3 solutions',
  type_mix: 'At least one explainer and one perspective',
  industry_in_batch: 'No two cards share a primary industry',
  term_vs_recent: 'No primary term used by a piece approved or published in the last 90 days',
};

export interface RecommendationRelaxation {
  constraint: RecommendationConstraint;
  reason: string;
}

export interface ContentRecommendationBatch {
  id: string;
  size_requested: number;
  idea_ids: string[];
  relaxed: RecommendationRelaxation[];
  /** Candidates considered, excluded (and why), solutions spanned, types. */
  report: Record<string, unknown> | null;
  created_by: string | null;
  created_at: string;
}

export interface SuggestRecommendationsRequest {
  /** How many cards (default 5, max 10). */
  count?: number;
}

export interface SuggestRecommendationsResult {
  batch: ContentRecommendationBatch;
  ideas: ContentIdea[];
}

export interface DecideRecommendationRequest {
  decision: 'approve' | 'reject';
  /** Required to reject. */
  reason?: string | null;
}

export interface ContentIdeaKeywordOption {
  id: string;
  keyword: string;
  cluster: string | null;
  intent: string | null;
  priority: string | null;
  target_url: string | null;
  status: string;
}

export interface ContentCorpusOption {
  id: string;
  url: string;
  title: string | null;
  publication: string | null;
  solution: string | null;
}

/*
  VRG-752 — the VERAfied guest types that lived here are GONE, with the second guest list.

  VRG-740 gave itself `gtm_verafied_guests` (name, company, role, email, LinkedIn, a
  suggested → contacted → booked → recorded / declined ladder, topic, notes, `content_idea_id`)
  while VRG-734 already had `verafied_guests` (the 39 imported guests, evaluation, influence,
  relationship, private notes, episode links, lead link). Two lists of the same people, whose
  symbols and hooks collided by name — at the 2026-10-23 merge these were renamed
  `IDEA_GUEST_STATUSES` / `IdeaGuestStatus` / `isIdeaGuestStatus` just so both would compile.

  Spencer chose the VERAfied list (2026-10-26). `drizzle/0172_verafied_guests_consolidate.sql`
  copies any rows across and gives it the `content_idea_id` this one had, so a guest's link to its
  interview idea survives. The one list's types are in `gtm/verafied.ts`; the idea a guest became
  is still a `ContentIdea` above. `gtm_verafied_guests` itself is kept until the copy is confirmed
  on every environment it reached (VRG-752) — nothing reads it.
*/
